import React from "react";
import { Box, Button } from "braid-design-system";
import Loading from "../components/Loading";
import ItemResourceListTable from "../components/ItemResource/ItemResourceListTable";
import CreateUpdateItemResourceDrawer from "../components/ItemResource/CreateUpdateItemResourceDrawer";
import { getAllItemLots, ItemLotResponse } from "../services/itemLotsService";
import { getAllItemResources, syncItemResourcesForApproval } from "../services/itemResourceService";
import { useToast } from "../hooks/useToast";
import { useCanManageMasters } from "../hooks/useCanManageMasters";

const ItemResourceManagement: React.FC = () => {
    const toast = useToast();
    const canManageMasters = useCanManageMasters();
    const [loading, setLoading] = React.useState(false);
    const [loadingMessage, setLoadingMessage] = React.useState<string | null>(null);
    const [itemLotList, setItemLotList] = React.useState<ItemLotResponse[]>([]);
    const [itemResourceMap, setItemResourceMap] = React.useState<Record<string, boolean>>({});
    const [approvedItemMap, setApprovedItemMap] = React.useState<Record<string, boolean>>({});
    const [searchTerm, setSearchTerm] = React.useState("");
    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const [selectedItemLot, setSelectedItemLot] = React.useState<ItemLotResponse | null>(null);
    const [selectedItemCodes, setSelectedItemCodes] = React.useState<Set<string>>(new Set());

    const fetchPageData = React.useCallback(async () => {
        try {
            setLoading(true);
            setLoadingMessage("Please wait, Fetching data...");

            const [itemLots, resources] = await Promise.all([
                getAllItemLots(),
                getAllItemResources(),
            ]);

            setItemLotList(itemLots);

            const resourceMap: Record<string, boolean> = {};
            const approvedMap: Record<string, boolean> = {};
            for (const res of resources) {
                if (res.resource_itemlots_code) {
                    resourceMap[res.resource_itemlots_code] = true;
                    if (res.is_approved === 1) {
                        approvedMap[res.resource_itemlots_code] = true;
                    }
                }
            }
            setItemResourceMap(resourceMap);
            setApprovedItemMap(approvedMap);
        } catch (error: any) {
            toast.showError(`Error fetching data: ${error?.message || "Unknown error"}`);
            setItemLotList([]);
            setItemResourceMap({});
            setApprovedItemMap({});
        } finally {
            setLoading(false);
            setLoadingMessage(null);
        }
    }, [toast]);

    React.useEffect(() => {
        fetchPageData();
    }, [fetchPageData]);

    const handleEditItemResource = React.useCallback((itemLot: ItemLotResponse) => {
        setSelectedItemLot(itemLot);
        setDrawerOpen(true);
    }, []);

    const handleToggleSelectItem = React.useCallback((itemlotsCode: string) => {
        setSelectedItemCodes((prev) => {
            const next = new Set(prev);
            if (next.has(itemlotsCode)) {
                next.delete(itemlotsCode);
            } else {
                next.add(itemlotsCode);
            }
            return next;
        });
    }, []);

    const handleToggleSelectAll = React.useCallback((itemlotsCodes: string[]) => {
        setSelectedItemCodes((prev) => {
            const allSelected = itemlotsCodes.length > 0 && itemlotsCodes.every((code) => prev.has(code));
            return allSelected ? new Set() : new Set(itemlotsCodes);
        });
    }, []);

    const handleSendSelectedForApproval = React.useCallback(async () => {
        const codes = Array.from(selectedItemCodes);
        if (codes.length === 0) return;

        try {
            setLoading(true);
            setLoadingMessage(`Sending ${codes.length} item(s) for approval...`);
            const results = await Promise.all(codes.map((code) => syncItemResourcesForApproval(code)));
            const totalSynced = results.reduce((sum, r) => sum + (r.synced_count || 0), 0);
            toast.showSuccess(totalSynced > 0
                ? `Successfully sent ${totalSynced} pending resource(s) across ${codes.length} item(s) for approval.`
                : "Selected item(s) had no pending resources to send for approval.");
            setSelectedItemCodes(new Set());
            await fetchPageData();
        } catch (error: any) {
            toast.showError(`Error sending resources for approval: ${error?.message || "Unknown error"}`);
        } finally {
            setLoading(false);
            setLoadingMessage(null);
        }
    }, [selectedItemCodes, toast, fetchPageData]);

    const handleCloseDrawer = React.useCallback(() => {
        setDrawerOpen(false);
        setSelectedItemLot(null);
    }, []);

    return (
        <>
            {loading && <Loading message={loadingMessage || "Loading..."} />}

            <Box padding="medium">
                <ItemResourceListTable
                    itemLotList={itemLotList}
                    itemResourceMap={itemResourceMap}
                    approvedItemMap={approvedItemMap}
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    onEditItemResource={canManageMasters ? handleEditItemResource : undefined}
                    selectedItemCodes={canManageMasters ? selectedItemCodes : undefined}
                    onToggleSelectItem={canManageMasters ? handleToggleSelectItem : undefined}
                    onToggleSelectAll={canManageMasters ? handleToggleSelectAll : undefined}
                    headerLeftAction={canManageMasters ? (
                        <Button
                            variant="solid"
                            tone={selectedItemCodes.size > 0 ? "brandAccent" : "neutral"}
                            onClick={handleSendSelectedForApproval}
                        >
                            {selectedItemCodes.size > 0
                                ? `Send to Approval (${selectedItemCodes.size})`
                                : "Send to Approval"}
                        </Button>
                    ) : null}
                />
            </Box>

            <CreateUpdateItemResourceDrawer
                isOpen={drawerOpen}
                onClose={handleCloseDrawer}
                selectedItemLot={selectedItemLot}
                onItemResourceSaved={fetchPageData}
                isReadOnly={!canManageMasters}
            />
        </>
    );
};

export default ItemResourceManagement;